// Accepting a suggestion replays its ops through the normal write path, so
// the changes are ordinary user ops from this device (spec v3 §4).
import { trackUse } from '@/state/usage'
import { api, engine, mutate } from '@/runtime'
import { db } from '@/runtime'
import { labelWords, PRIORITY_WORD } from '@/lib/words'
import { KindError } from '@/kinds/kind'
import { blocksToText } from '@/kinds/page/text'
import { markdownToBlocks } from '@/kinds/page/markdown'
import type { PageResource } from '@/kinds/page/apply'
import type { SuggestionOp, SuggestionResource } from '@/kinds/suggestion'
import { isMergeCard, mergeTargets } from '@/lib/mergeQueue'
import { prKey } from '@/lib/flow'
import { fmtDateShort, fmtDateInPhrase } from '@/lib/time'
import { useUi } from '@/state/ui'

/** The steps a create op brings with it (args.subtasks), blank ones dropped. */
export const subtasksOf = (args: unknown): { title: string; estimate_minutes?: number }[] => {
  const a = (args ?? {}) as { subtasks?: unknown }
  if (!Array.isArray(a.subtasks)) return []
  return a.subtasks
    .map((x) => {
      const o = (x ?? {}) as { title?: unknown; estimate_minutes?: unknown }
      const title = typeof o.title === 'string' ? o.title.trim() : ''
      const est =
        typeof o.estimate_minutes === 'number' && o.estimate_minutes > 0 ? o.estimate_minutes : undefined
      return { title, estimate_minutes: est }
    })
    .filter((x) => x.title)
    .slice(0, 20)
}

/**
 * Replay one op template through the normal write path. `linkTo` is the page a
 * suggestion targets: tasks created from it get a references edge back to it.
 */
const replay = async (
  op: SuggestionOp,
  linkTo?: string,
  from?: { session?: string; ws?: string },
): Promise<{ kind: string; id: string; steps?: string[] } | undefined> => {
  if (op.type === 'create' && op.spec) {
    let spec = op.spec
    if (op.kind === 'Page') {
      // the assistant writes markdown in spec.text; the editor wants blocks
      const md = String(spec.text ?? '')
      const blocks = md.trim() ? await markdownToBlocks(md) : []
      spec = { ...spec, blocks, text: blocksToText(blocks) }
    }
    const r = await mutate.create(op.kind, spec, op.labels ?? {})
    if (linkTo && op.kind === 'Task') await mutate.setEdge(r.id, linkTo, 'references')
    // made under something: a page or list under a project, task or page; a sub-project
    const under = (op.args as { under?: string } | undefined)?.under
    const holder = under ? await db.resources.get(under) : undefined
    // with a person it is a reference (a task with or for them, their birthday); else the holder
    if (under && holder?.kind === 'Person') await mutate.setEdge(r.id, under, 'references')
    else if (under) await mutate.setProject(r.id, under)
    // the people it is about: "me and Keira" is a link to each
    for (const pid of peopleOf(op.args)) if (pid !== under) await mutate.setEdge(r.id, pid, 'references')
    // a task's steps: each a task on the parent's day, tied to it by a
    // references edge, the way the task page's "Make sub-tasks" does
    const steps: string[] = []
    if (op.kind === 'Task') {
      for (const st of subtasksOf(op.args)) {
        const child = await mutate.create(
          'Task',
          {
            title: st.title,
            ...(st.estimate_minutes ? { estimate_minutes: st.estimate_minutes } : {}),
            ...(typeof spec.scheduled === 'string' ? { scheduled: spec.scheduled } : {}),
            ...(typeof spec.due === 'string' ? { due: spec.due } : {}),
          },
          op.labels ?? {},
        )
        await mutate.setEdge(child.id, r.id, 'references')
        steps.push(child.id)
      }
    }
    return { kind: op.kind, id: r.id, ...(steps.length ? { steps } : {}) }
  } else if (op.type === 'page_write' && op.id) {
    const a = (op.args ?? {}) as { mode?: string; markdown?: string }
    const page = (await db.resources.get(op.id)) as PageResource | undefined
    if (!page || page.kind !== 'Page') throw new KindError('invalid', 'page no longer exists')
    const added = await markdownToBlocks(String(a.markdown ?? ''))
    // a page with no blocks is its markdown text (written outside the editor, or
    // opened and not edited since): append to that, not to nothing
    const base =
      page.spec.blocks.length || !page.spec.text?.trim()
        ? page.spec.blocks
        : await markdownToBlocks(page.spec.text)
    const blocks = a.mode === 'replace' ? added : [...base, ...added]
    await mutate.patch(op.id, { spec: { blocks, text: blocksToText(blocks) } })
  } else if (op.type === 'link' && op.id) {
    const a = (op.args ?? {}) as { project_id?: string; parent_id?: string; relation?: string }
    const parent = a.parent_id || a.project_id || null
    if (a.relation === 'references' && parent) {
      // a step of another task has one parent task, so an earlier one goes;
      // tasks and dates may be about several people, so a person drops nothing
      const target = await db.resources.get(parent)
      const single = target?.kind === 'Task' ? 'Task' : undefined
      const mine = (await db.edges.where('from_id').equals(op.id).toArray()).filter(
        (e) => e.relation === 'references' && !e.deleted_at && e.to_id !== parent,
      )
      for (const e of mine) {
        const to = await db.resources.get(e.to_id)
        if (single && to?.kind === single) await mutate.setEdge(op.id, e.to_id, 'references', true)
      }
      await mutate.setEdge(op.id, parent, 'references')
    } else {
      // belongs_to: the one generic re-parent (a task's project, a page's or
      // list's holder, a project's parent)
      await mutate.setProject(op.id, parent)
    }
  } else if (op.type === 'action' && op.action && op.id) {
    await mutate.action(op.id, op.action, op.args ?? {})
  } else if (op.type === 'patch' && op.spec && op.id) {
    await mutate.patch(op.id, { spec: op.spec })
  } else if (op.type === 'delete' && op.id) {
    // an API token asking to remove something: to the Trash, like any delete
    await mutate.remove(op.id)
  } else if (op.type === 'start_agent') {
    // a coding agent is the factory's, not a record here: the hand-off is a call
    // (online only, outside undo); its AgentRun mirror arrives through sync
    const a = (op.args ?? {}) as {
      repo?: string
      prompt?: string
      title?: string
      task_id?: string
      workspace?: string
      project_id?: string
      model?: string
    }
    if (!a.prompt || !(a.repo || a.project_id))
      throw new KindError('invalid', 'a coding agent needs a brief and a repo or project')
    // it belongs where the card was made, and reports to the thread that made it
    const workspace = a.workspace ?? from?.ws
    const r = await api.factoryHandOff({
      repo: a.repo ?? '',
      prompt: a.prompt,
      ...(a.title ? { title: a.title } : {}),
      ...(a.task_id ? { task_id: a.task_id } : {}),
      ...(a.project_id ? { project_id: a.project_id } : {}),
      ...(workspace ? { workspace } : {}),
      ...(from?.session ? { session: from.session } : {}),
      ...(a.model === 'opus' || a.model === 'sonnet' ? { model: a.model } : {}),
    })
    await engine.sync() // so its thread is here to open
    return { kind: 'AgentRun', id: r.id }
  } else if (op.type === 'create_prototype') {
    // one of the owner's ideas becomes a private repo made from the prototype template
    // (factory contract and agents' briefing); Prime builds it with a swarm from there
    const a = (op.args ?? {}) as { name?: string; description?: string }
    if (!a.name) throw new KindError('invalid', 'a prototype needs a name')
    await api.factoryPrototype(a.name, a.description ?? '')
  } else if (op.type === 'merge_pr') {
    // Prime's review came out well: squash and merge, like the review page's button
    const a = (op.args ?? {}) as { repo?: string; number?: number }
    if (!a.repo || !a.number) throw new KindError('invalid', 'a merge needs a repo and a number')
    await api.reviewMerge(a.repo, a.number, 'squash', true)
    trackUse('action:merge')
  } else if (op.type === 'release') {
    // ship a built commit of main to prod (docs/shipping.md)
    const a = (op.args ?? {}) as { sha?: string }
    if (!a.sha) throw new KindError('invalid', 'a release needs a commit')
    await api.release(a.sha)
    trackUse('action:ship')
  }
}

type Made = { op: number; kind: string; id: string }

/**
 * Accept the ticked ops (all when `keep` is omitted); the rest are simply not
 * applied. Resolves to what the replay made, so a caller can open it.
 */
export const acceptSuggestion = async (
  s: SuggestionResource,
  keep?: number[],
  /** the option tapped, on an ask (kept as status.answer) */
  answer?: string,
): Promise<Made[]> => {
  // a card that only merges is queued on the server (one merge at a time a repo, in
  // tap order); the card shows Queued, Merging, Merged or why it failed as it syncs
  if (isMergeCard(s) && (keep === undefined || keep.length === s.spec.ops.length)) {
    await api.mergeQueue(s.id)
    trackUse('action:merge')
    return []
  }
  // a card that merges several ("merge everything") queues each pull request as the
  // Pipeline screen's Merge all does, so the queue stays the one place merges run
  const several = mergeTargets(s)
  if (several.length > 1 && (keep === undefined || keep.length === s.spec.ops.length)) {
    const { results } = await api.pipelineMerge(several.map((t) => prKey(t.repo, t.number)))
    await mutate.bookkeeping(() => mutate.action(s.id, 'accept', {}))
    trackUse('action:merge')
    const pr = (item: string) => item.slice(item.lastIndexOf('#'))
    const refused = results.filter((r) => r.error)
    useUi
      .getState()
      .showFlash(
        refused.length
          ? `Not queued: ${refused.map((r) => `${pr(r.item)} ${r.error}`).join('; ')}`
          : `Queued ${results.map((r) => pr(r.item)).join(', ')}`,
        refused.length ? 6000 : 2500,
      )
    return []
  }
  return mutate.grouped(() => acceptInner(s, keep, answer))
}

const acceptInner = async (s: SuggestionResource, keep?: number[], answer?: string): Promise<Made[]> => {
  const indexes = keep ?? s.spec.ops.map((_, i) => i)
  let applied = 0
  const created: Made[] = []
  const target = s.spec.target_id ? await db.resources.get(s.spec.target_id) : undefined
  const linkTo = target?.kind === 'Page' ? target.id : undefined
  for (const i of indexes) {
    const op = s.spec.ops[i]
    if (!op) continue
    try {
      const made = await replay(op, linkTo, { session: s.spec.session_id, ws: s.labels.ws })
      if (made) {
        created.push({ op: i, kind: made.kind, id: made.id })
        // the steps it made are the capture's too: kept so a whole capture can be dropped
        for (const id of made.steps ?? []) created.push({ op: i, kind: 'Task', id })
      }
      applied++
    } catch (e) {
      // a target may have changed since the proposal; skip it, keep going
      if (!(e instanceof KindError)) throw e
    }
  }
  // the card recording its own decision is not a change to undo
  await mutate.bookkeeping(() =>
    mutate.action(s.id, 'accept', {
      ...(keep ? { ops: keep } : {}),
      ...(created.length ? { created } : {}),
      ...(answer ? { answer } : {}),
    }),
  )
  useUi
    .getState()
    .showFlash(
      applied === indexes.length
        ? applied
          ? 'Done'
          : 'Nothing applied'
        : `Applied ${applied} of ${indexes.length}`,
      2500,
    )
  return created
}

/** A card's own decision is not a change to the owner's things: undo leaves it alone. */
export const dismissSuggestion = (s: SuggestionResource) =>
  mutate.bookkeeping(() => mutate.action(s.id, 'dismiss'))

/** Mark a capture receipt as seen: it leaves Today and stays in the captures log. */
export const acknowledgeCapture = (s: SuggestionResource) =>
  mutate.bookkeeping(() => mutate.action(s.id, 'acknowledge'))

/**
 * Undo a capture the assistant filed: restore the task's fields, state and
 * project from `status.before`, delete anything the capture created, then mark
 * the receipt reverted. Ordinary ops from this device, like everything else.
 */
export const revertCapture = (s: SuggestionResource): Promise<void> => mutate.grouped(() => revertInner(s)) // one press of undo takes the whole put-back back

const revertInner = async (s: SuggestionResource): Promise<void> => {
  const b = s.status.before
  const taskId = s.spec.target_id
  if (taskId && b) {
    const task = await db.resources.get(taskId)
    if (task && !task.deleted_at) {
      const spec: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(b.spec ?? {})) spec[k] = v === undefined ? null : v
      const labels: Record<string, string | null> = {}
      for (const [k, v] of Object.entries(b.labels ?? {})) labels[k] = v ?? null
      if (Object.keys(spec).length || Object.keys(labels).length)
        await mutate.patch(taskId, {
          ...(Object.keys(spec).length ? { spec } : {}),
          ...(Object.keys(labels).length ? { labels } : {}),
        })
      const st = (task.status as { state?: string }).state
      // a capture filed into a list was dropped as its carrier: bring it back first
      if ((st === 'dropped' || st === 'done') && b.state)
        await mutate.action(taskId, 'reopen').catch(() => {})
      if (b.state && b.state !== st && ['inbox', 'next', 'waiting', 'someday'].includes(b.state))
        await mutate.action(taskId, 'move', { state: b.state }).catch(() => {})
      if (b.project_id !== undefined)
        await mutate.setProject(taskId, b.project_id === '-' ? null : b.project_id)
    }
  }
  // list actions run backwards: a tick is unticked, a removal re-added, an addition taken off
  for (const rec of [...(b?.lists ?? [])].reverse()) {
    const items = rec.items as { id?: string; text?: string; done?: boolean; added_by?: string }[]
    try {
      if (rec.action === 'tick')
        for (const it of items) if (it.id) await mutate.action(rec.list_id, 'untick', { id: it.id })
      if (rec.action === 'untick')
        for (const it of items) if (it.id) await mutate.action(rec.list_id, 'tick', { id: it.id })
      if (rec.action === 'remove_item' && items.length)
        await mutate.action(rec.list_id, 'add_items', {
          items: items.map((it) => ({ id: it.id, text: it.text, added_by: it.added_by })),
        })
      if (rec.action === 'add_items')
        for (const it of items) if (it.id) await mutate.action(rec.list_id, 'remove_item', { id: it.id })
    } catch {
      /* the list may have moved on; the rest still reverts */
    }
  }
  for (const c of s.status.created ?? []) await mutate.remove(c.id).catch(() => {})
  await mutate.bookkeeping(() => mutate.action(s.id, 'revert'))
  useUi.getState().showFlash('Put back', 2000)
}

/** Ask the server's assistant; suggestions arrive on the next sync. */
export const askAssistant = async (workspace: string, prompt: string, mode: 'ask' | 'plan' = 'ask') => {
  const res = await api.assistantAsk({ workspace, prompt, mode })
  return res.suggestions.length
}

/** Send a note back on a suggestion; the server supersedes it with a revised one. */
export const refineSuggestion = async (s: SuggestionResource, feedback: string) => {
  const res = await api.assistantAsk({ mode: 'refine', suggestion_id: s.id, prompt: feedback })
  return res.suggestions.length
}

/** Human line for one op, e.g. “Schedule ‘Call mum’ for Tomorrow”. */
const under = (op: SuggestionOp, titleOf: (id: string) => string | undefined) => {
  const u = (op.args as { under?: string } | undefined)?.under
  return u ? ` under “${titleOf(u) ?? '…'}”` : ''
}

/** The people a new date or task is about (args.people). */
const peopleOf = (args: unknown): string[] => {
  const p = (args as { people?: unknown } | undefined)?.people
  return Array.isArray(p) ? p.filter((x): x is string => typeof x === 'string') : []
}

/** " for Sam", " for You and Keira" when made for people, else the holder it goes under. */
const underOrFor = (
  op: SuggestionOp,
  titleOf: (id: string) => string | undefined,
  kindOf: (id: string) => string | undefined,
) => {
  const u = (op.args as { under?: string } | undefined)?.under
  const ids = [...(u && kindOf(u) === 'Person' ? [u] : []), ...peopleOf(op.args).filter((x) => x !== u)]
  if (ids.length) {
    const names = ids.map((id) => titleOf(id) ?? '…')
    return ` for ${names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0]}`
  }
  return under(op, titleOf)
}

const steps = (op: SuggestionOp) => {
  const n = subtasksOf(op.args).length
  return n ? ` with ${n} step${n === 1 ? '' : 's'}` : ''
}

export const describeOp = (
  op: SuggestionOp,
  titleOf: (id: string) => string | undefined,
  kindOf: (id: string) => string | undefined = () => undefined,
): string => {
  const name = op.id ? (titleOf(op.id) ?? op.kind) : ''
  const q = (t: string) => `“${t}”`
  if (op.type === 'page_write') {
    const a = (op.args ?? {}) as { mode?: string; markdown?: string }
    const words = String(a.markdown ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean).length
    return `${a.mode === 'replace' ? 'Rewrite' : 'Append to'} page ${q(name)} (${words} words)`
  }
  if (op.type === 'delete') return `Move ${q(name)} to the Trash`
  if (op.type === 'merge_pr') {
    const a = (op.args ?? {}) as { repo?: string; number?: number }
    return `Squash and merge PR #${a.number ?? '…'} on ${a.repo ?? '…'}`
  }
  // the build is the card's business, not the owner's: a commit hash says nothing to read
  if (op.type === 'release') return 'Put the build on prod'
  if (op.type === 'create_prototype') {
    const a = (op.args ?? {}) as { name?: string; description?: string }
    return `Set up a prototype: a private repo alfiemills/${a.name ?? '…'}${a.description ? ` (${a.description})` : ''}`
  }
  if (op.type === 'start_agent') {
    const a = (op.args ?? {}) as { repo?: string; title?: string; prompt?: string }
    const what = a.title || String(a.prompt ?? '').slice(0, 80)
    return `Start a coding agent on ${a.repo ?? '…'}: ${q(what)}`
  }
  if (op.type === 'link') {
    const a = (op.args ?? {}) as { project_id?: string; parent_id?: string; relation?: string }
    const parent = a.parent_id || a.project_id
    const target = parent ? (titleOf(parent) ?? '…') : '…'
    if (a.relation === 'references' && parent && kindOf(parent) === 'Person')
      return `Link ${q(name)} to ${target}`
    if (a.relation === 'references') return `Make ${q(name)} a step of ${q(target)}`
    if (a.project_id) return `Put ${q(name)} in project ${q(target)}`
    return `Put ${q(name)} under ${q(target)}`
  }
  if (op.type === 'create') {
    const t = (op.spec?.title as string | undefined) ?? 'untitled'
    if (op.kind === 'Page') return `Add page ${q(t)}${underOrFor(op, titleOf, kindOf)}`
    if (op.kind === 'List') {
      const items = Array.isArray(op.spec?.items) ? (op.spec!.items as { text?: string }[]) : []
      return `Add list ${q(t)}${
        items.length
          ? ` with ${items
              .map((i) => i.text ?? '')
              .filter(Boolean)
              .join(', ')}`
          : ''
      }`
    }
    if (op.kind === 'Person') {
      const n = String(op.spec?.name ?? 'someone')
      return `Add ${n}${op.spec?.circle ? ` (${String(op.spec.circle)})` : ''} to People`
    }
    if (op.kind === 'Date') {
      const d = String(op.spec?.date ?? '')
      const r =
        (
          {
            yearly: ', every year',
            monthly: ', every month',
            weekly: ', every week',
            fortnightly: ', every other week',
          } as Record<string, string>
        )[String(op.spec?.repeat)] ?? ''
      const at = op.spec?.time ? ` at ${String(op.spec.time)}` : ''
      const where = op.spec?.place ? `, ${String(op.spec.place)}` : ''
      // an appointment is booked; a date is remembered
      const verb = op.spec?.time ? 'Book' : 'Remember'
      return `${verb} ${q(t)}${d ? ` (${fmtDateShort(d)}${at}${where}${r})` : ''}${underOrFor(op, titleOf, kindOf)}`
    }
    const bits: string[] = []
    if (op.spec?.scheduled) bits.push(fmtDateShort(String(op.spec.scheduled)))
    if (op.spec?.estimate_minutes) bits.push(`${op.spec.estimate_minutes}m`)
    return `Add ${op.kind === 'Project' ? 'project' : 'task'} ${q(t)}${bits.length ? ` (${bits.join(', ')})` : ''}${steps(op)}${underOrFor(op, titleOf, kindOf)}`
  }
  if (op.type === 'action') {
    const a = (op.args ?? {}) as Record<string, unknown>
    if (op.kind === 'List') {
      const items = Array.isArray(a.items) ? (a.items as { text?: string }[]) : []
      const item = typeof a.text === 'string' ? q(a.text) : 'an item'
      switch (op.action) {
        case 'add_items':
          return `Add to list ${q(name)}: ${items
            .map((i) => i.text ?? '')
            .filter(Boolean)
            .join(', ')}`
        case 'tick':
          return `Tick ${item} on ${q(name)}`
        case 'untick':
          return `Untick ${item} on ${q(name)}`
        case 'remove_item':
          return `Take ${item} off ${q(name)}`
        case 'clear_ticked':
          return `Clear ticked items on ${q(name)}`
        case 'reset':
          return `Reset ${q(name)}`
        default:
          return `${op.action} on list ${q(name)}`
      }
    }
    if (op.kind === 'AgentRun' && op.action === 'say') return `Tell ${q(name)}: ${q(String(a.text ?? ''))}`
    switch (op.action) {
      case 'defer':
        return `Schedule ${q(name)} for ${a.scheduled ? fmtDateInPhrase(String(a.scheduled)) : '…'}${a.scheduled_time ? ` at ${a.scheduled_time}` : ''}`
      case 'move':
        // the state in words, never the enum value
        switch (String(a.state)) {
          case 'next':
            return `Make ${q(name)} next`
          case 'someday':
            return `Park ${q(name)} in Someday`
          case 'waiting':
            return `Mark ${q(name)} as waiting`
          case 'inbox':
            return `Put ${q(name)} back in the Inbox`
          default:
            return `Move ${q(name)}`
        }
      case 'touch':
        return `Keep ${q(name)} as it is`
      case 'complete':
        return `Complete ${q(name)}`
      case 'drop':
        return `Drop ${q(name)}${typeof a.reason === 'string' && a.reason ? `: ${a.reason}` : ''}`
      default:
        return `${op.action} ${q(name)}`
    }
  }
  const keys = Object.keys(op.spec ?? {})
  const labelKeys = Object.keys(op.labels ?? {})
  if (keys.length === 0 && labelKeys.length)
    return `Label ${q(name)} ${labelKeys.map((k) => labelWords(k, String(op.labels![k]))).join(', ')}`
  if (keys.length === 1 && keys[0] === 'checklist') {
    const n = Array.isArray(op.spec?.checklist) ? (op.spec!.checklist as unknown[]).length : 0
    return `Set ${n} checklist step${n === 1 ? '' : 's'} on ${q(name)}`
  }
  const bits = keys.map((k) => {
    const v = op.spec![k]
    // each field in words: what it becomes, not the field's name
    if (k === 'title') return `rename to ${q(String(v))}`
    if (k === 'estimate_minutes') return `${String(v)}m`
    if (k === 'priority') return `${(PRIORITY_WORD[Number(v)] ?? 'Normal').toLowerCase()} priority`
    if (k === 'due') return `due ${fmtDateInPhrase(String(v))}`
    if (k === 'scheduled') return v ? `for ${fmtDateInPhrase(String(v))}` : 'no day'
    if (k === 'scheduled_time') return v ? `at ${String(v)}` : 'no time'
    if (k === 'notes') return 'new notes'
    if (k === 'remind_at') return v ? 'a reminder' : 'no reminder'
    if (k === 'recurrence') return 'how it repeats'
    if (k === 'after') return 'what it waits for'
    if (k === 'every_days') return `every ${String(v)} days`
    return k.replace(/_/g, ' ')
  })
  if (labelKeys.length) bits.push(labelKeys.map((k) => labelWords(k, String(op.labels![k]))).join(', '))
  return `Edit ${q(name)}: ${bits.join(', ')}`
}

/**
 * File a put-back capture again: replay its ops in order, mapping anything the
 * first filing created (since deleted) onto what this replay makes, then mark
 * the receipt applied. `before` still describes what arrived, so another
 * put-back works.
 */
export const reapplyCapture = (s: SuggestionResource): Promise<void> => mutate.grouped(() => reapplyInner(s))

const reapplyInner = async (s: SuggestionResource): Promise<void> => {
  const oldCreated = new Map((s.status.created ?? []).map((c) => [c.op, c.id]))
  const remap = new Map<string, string>()
  const swap = (id?: string) => (id && remap.get(id)) || id
  const created: { op: number; kind: string; id: string }[] = []
  for (const [i, op] of s.spec.ops.entries()) {
    const args =
      op.args && typeof op.args === 'object' && 'project_id' in op.args
        ? { ...op.args, project_id: swap(String((op.args as { project_id?: string }).project_id ?? '')) }
        : op.args
    try {
      const made = await replay({ ...op, id: swap(op.id), args })
      if (made) {
        created.push({ op: i, ...made })
        const old = oldCreated.get(i)
        if (old) remap.set(old, made.id)
      }
    } catch (e) {
      // a target may have moved on since; keep going with the rest
      if (!(e instanceof KindError)) throw e
    }
  }
  await mutate.bookkeeping(() => mutate.action(s.id, 'reapply', created.length ? { created } : {}))
  useUi.getState().showFlash('Filed again', 2000)
}

/**
 * Do what the agent proposed, straight away, as one undoable action. Used by
 * a session when "let the agent do it" is on: the ops are replayed through the
 * ordinary write path, so they are the owner's own writes, they sync like
 * anything else, and ⌘Z takes the whole turn back.
 */
export const applyForAgent = async (s: SuggestionResource): Promise<number> => {
  // starting a coding agent spends real resources and can push code, and a merge
  // lands code on main, and a release ships to prod: those cards always wait for
  // the owner's tap (docs/factory.md, docs/shipping.md)
  if (
    s.spec.ops.some(
      (op) =>
        op.type === 'start_agent' ||
        op.type === 'merge_pr' ||
        op.type === 'release' ||
        op.type === 'create_prototype',
    )
  )
    return 0
  await acceptSuggestion(s) // already one group, so undo takes the whole turn back
  return s.spec.ops.length
}
